
import React, { useState, useEffect } from "react";
import { Cardholder } from "@/api/entities";
import { Transaction } from "@/api/entities";
import { Dispute } from "@/api/entities";
import { format } from "date-fns";
import { AlertCircle, Check } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

import PageHeader from "../components/PageHeader";
import CardholderSection from "../components/CardholderSection";
import CardSelectionSection from "../components/CardSelectionSection";
import DisputeReasonSection from "../components/DisputeReasonSection";
import TransactionsSection from "../components/TransactionsSection";
import DisputeQuestionsSection from "../components/DisputeQuestionsSection";
import DisputePreviewSection from "../components/DisputePreviewSection";
import SuccessPage from "../components/SuccessPage";

export default function DisputeIntake() {
  const [selectedCardholder, setSelectedCardholder] = useState(null);
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [disputeData, setDisputeData] = useState(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);
  const [disputeId, setDisputeId] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [disputeReason, setDisputeReason] = useState(null);
  
  const handleCardholderSelect = (cardholder) => {
    setSelectedCardholder(cardholder);
    
    // Reset subsequent sections when cardholder changes
    if (!cardholder) {
      setSelectedTransactions([]);
      setDisputeData(null);
      setIsPreviewMode(false);
      setSelectedCard(null);
      setDisputeReason(null);
    }
  };

  const handleCardSelect = (card) => {
    setSelectedCard(card);
    
    // Reset subsequent sections when card changes
    if (!card) {
      setSelectedTransactions([]);
      setDisputeData(null);
      setIsPreviewMode(false);
      setDisputeReason(null);
    }
  };
  
  const handleDisputeReasonChange = (data) => {
    setDisputeReason(data);
    
    // Reset subsequent sections when dispute reason changes
    setSelectedTransactions([]); // Always reset transactions

    if (data) {
      // If a new reason is selected
      setDisputeData(prevData => ({
        ...(prevData || {}), // Preserve other parts of disputeData if they exist
        disputeCategory: data.category,
        disputeType: data.type,
        responses: {} // Reset responses for the new dispute type
      }));
    } else {
      // If the reason is cleared (data is null)
      setDisputeData(prevData => ({
        ...(prevData || {}),
        disputeCategory: null,
        disputeType: null,
        responses: {} // Clear responses
      }));
    }
  };
  
  const handleTransactionsSelect = (transactions) => {
    setSelectedTransactions(transactions);
    
    // Reset dispute question responses when transactions change
    if (transactions.length === 0) {
      setDisputeData(prevData => ({
        ...(prevData || {}), // Preserve other parts of disputeData
        responses: {}
      }));
      setIsPreviewMode(false);
    }
  };
  
  const handleDisputeDataChange = (data) => {
    setDisputeData({
      ...disputeData,
      ...data
    });
  };
  
  const handlePreview = () => {
    setIsPreviewMode(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const handleBackFromPreview = () => {
    setIsPreviewMode(false);
  };
  
  const handleNewDispute = () => {
    // Reset everything to start a new dispute
    setSelectedCardholder(null);
    setSelectedCard(null);
    setSelectedTransactions([]);
    setDisputeData(null);
    setDisputeReason(null);
    setIsPreviewMode(false);
    setSubmissionSuccess(false);
    setDisputeId(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Helper function to check if multiple transactions are allowed
  const isMultipleTransactionsAllowed = () => {
    if (!disputeReason) return false;
    
    const multipleAllowedTypes = [
      "Lost card",
      "Stolen card", 
      "Card not received"
    ];
    
    return multipleAllowedTypes.includes(disputeReason.type);
  };
  
  const handleSubmit = async () => {
    if (!selectedCardholder || selectedTransactions.length === 0 || !disputeData) {
      setSubmissionError("Missing required information. Please complete all sections.");
      return;
    }
    
    setIsSubmitting(true);
    setSubmissionError(null);
    
    try {
      // Generate a unique dispute ID (simple random string since uuid is not available)
      const newDisputeId = Math.random().toString(36).substring(2, 10).toUpperCase();
      setDisputeId(newDisputeId);
      
      // Prepare dispute data
      const disputeFormData = {
        cardholder_id: selectedCardholder.id,
        transaction_ids: selectedTransactions.map(t => t.id),
        dispute_category: disputeData.disputeCategory,
        dispute_type: disputeData.disputeType,
        responses: disputeData.responses,
        status: "Submitted",
        submission_date: new Date().toISOString(),
        evidence_files: [] // In a real app, you would have the uploaded file URLs here
      };
      
      // Submit the dispute
      await Dispute.create(disputeFormData);
      
      // Show success
      setSubmissionSuccess(true);
      
    } catch (error) {
      console.error("Error submitting dispute:", error);
      setSubmissionError("An error occurred while submitting the dispute. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Set page title
  React.useEffect(() => {
    document.title = "Intake form by Efficio";
  }, []);
  
  // Add mock data for testing when the component loads
  useEffect(() => {
    const addMockData = async () => {
      try {
        // Add Jessica Morgan if she doesn't exist
        const existingCardholders = await Cardholder.list();
        if (existingCardholders.length === 0) {
          await Cardholder.create({
            full_name: "Jessica Morgan",
            email: "jessica.morgan.jm@gmail.com",
            phone: "+1 (212) 555-5555",
            account_id: "1248962"
          });
        }
        
        // Add mock transactions if none exist
        const existingTransactions = await Transaction.list();
        if (existingTransactions.length === 0) {
          const today = new Date();
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          
          const twoDaysAgo = new Date(today);
          twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);
          
          await Transaction.bulkCreate([
            {
              merchant: "Uber Eats",
              amount: 45.23,
              date: format(today, "yyyy-MM-dd"),
              settlement_status: "Pending",
              type: "Online",
              notes: "Incorrect Amount Billed"
            },
            {
              merchant: "Lyft",
              amount: 24.30,
              date: format(twoDaysAgo, "yyyy-MM-dd"),
              settlement_status: "Settled",
              type: "Online",
              notes: "Lost Card"
            },
            {
              merchant: "DoorDash",
              amount: 43.90,
              date: format(twoDaysAgo, "yyyy-MM-dd"),
              settlement_status: "Settled",
              type: "Online",
              notes: "Lost Card"
            },
            {
              merchant: "Target",
              amount: 829.99,
              date: format(yesterday, "yyyy-MM-dd"),
              settlement_status: "Settled",
              type: "Online",
              notes: "Lost Card"
            },
            {
              merchant: "PlayStation Network",
              amount: 59.99,
              date: format(yesterday, "yyyy-MM-dd"),
              settlement_status: "Settled",
              type: "Digital Purchase",
              notes: "Lost Card"
            },
            {
              merchant: "Nike.com",
              amount: 199.95,
              date: format(yesterday, "yyyy-MM-dd"),
              settlement_status: "Settled",
              type: "Online",
              notes: "Lost Card"
            },
            {
              merchant: "Amazon",
              amount: 100.00,
              date: format(yesterday, "yyyy-MM-dd"),
              settlement_status: "Settled",
              type: "Online",
              notes: "Lost Card"
            },
            {
              merchant: "Delta Airlines",
              amount: 250.00,
              date: format(today, "yyyy-MM-dd"),
              settlement_status: "Pending",
              type: "Online",
              notes: "Lost Card"
            },
            {
              merchant: "Spotify",
              amount: 19.99,
              date: "2025-04-20",
              settlement_status: "Settled",
              type: "Online (Recurring)",
              notes: "Cancelled Recurring"
            },
            {
              merchant: "Westwood Goods",
              amount: 102.04,
              date: "2025-03-11",
              settlement_status: "Settled",
              type: "Online",
              notes: "Merchandise Not Received"
            },
            {
              merchant: "Best Buy",
              amount: 290.48,
              date: "2025-04-21",
              settlement_status: "Settled",
              type: "Online",
              notes: "Unauthorized – Card in Possession"
            },
            {
              merchant: "Boscov's",
              amount: 47.20,
              date: "2025-01-15",
              settlement_status: "Settled",
              type: "Online",
              notes: "Expired Dispute (90-Day Rule)"
            }
          ]);
        }
      } catch (error) {
        console.error("Error adding mock data:", error);
      }
    };
    
    addMockData();
  }, []);

  return (
    <>
      <PageHeader />
      <div className="container mx-auto py-8 px-4">
        {!submissionSuccess && (
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Dispute Intake Form</h1>
            <p className="text-gray-600">
              Use this form to submit disputes on behalf of cardholders
            </p>
          </div>
        )}
        
        {submissionError && (
          <Alert className="mb-6" variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{submissionError}</AlertDescription>
          </Alert>
        )}
        
        {submissionSuccess ? (
          <SuccessPage 
            disputeId={disputeId}
            disputeData={disputeData}
            selectedCardholder={selectedCardholder}
            selectedTransactions={selectedTransactions}
            onNewDispute={handleNewDispute}
          />
        ) : isPreviewMode ? (
          <DisputePreviewSection 
            isPreviewMode={isPreviewMode}
            selectedCardholder={selectedCardholder}
            selectedCard={selectedCard}
            selectedTransactions={selectedTransactions}
            disputeData={disputeData}
            disputeReason={disputeReason}
            onBack={handleBackFromPreview}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
          />
        ) : (
          <>
            <CardholderSection 
              selectedCardholder={selectedCardholder} 
              onCardholderSelect={handleCardholderSelect}
            />
            
            <CardSelectionSection
              selectedCardholder={selectedCardholder}
              selectedCard={selectedCard}
              onCardSelect={handleCardSelect}
              disabled={!selectedCardholder}
            />
            
            <DisputeReasonSection
              selectedCardholder={selectedCardholder}
              selectedCard={selectedCard}
              disputeReason={disputeReason}
              onDisputeReasonChange={handleDisputeReasonChange}
              disabled={!selectedCardholder || !selectedCard}
            />
            
            <TransactionsSection 
              selectedCardholder={selectedCardholder}
              selectedCard={selectedCard}
              selectedTransactions={selectedTransactions}
              onTransactionsSelect={handleTransactionsSelect}
              disabled={!selectedCardholder || !selectedCard || !disputeReason}
              allowMultiple={isMultipleTransactionsAllowed()}
              disputeType={disputeReason?.type}
            />
            
            <DisputeQuestionsSection 
              selectedCardholder={selectedCardholder}
              selectedCard={selectedCard}
              selectedTransactions={selectedTransactions}
              onDisputeDataChange={handleDisputeDataChange}
              disputeData={disputeData}
              disputeReason={disputeReason}
              onPreview={handlePreview}
              disabled={!selectedCardholder || !selectedCard || !disputeReason || selectedTransactions.length === 0}
            />
          </>
        )}
      </div>
    </>
  );
}
